import { z } from 'zod';
import { NO_ROLE_FLAG } from '#components/patterns/entity-select/role/option-constants.ts';
import {
  checkDomainFormat,
  checkNewDomain,
  domainFieldsSchema,
  INVALID_DOMAIN_NAME_ERR,
  NEW_DOMAIN_NAME_ERR,
} from '../domainFields.schema.ts';
import {
  addIdentityProviderBaseFormSchema,
  editIdentityProviderBaseFormSchema,
} from '../IdentityProviderBaseForm.schema.ts';
import { convertToHours } from './convertToHours.ts';

/** The API uses null to indiciate no expiration */
export const LIFETIME_FOREVER_FLAG = null;
export const LIFETIME_FOREVER_UNIT = 'No expiration';
export const LIFETIME_UNITS = ['Hours', 'Days', 'Weeks', 'Months', LIFETIME_FOREVER_UNIT] as const;

const MAX_HOURS = 43800;
const TOO_MANY_HOURS_ERR = {
  message: 'Must be shorter than 5 years',
  path: ['certLifetime'],
};

const endpointFieldsSchema = {
  name: z.string().min(1, 'Please choose a name for this provider').max(50, 'Name cannot be longer than 50 characters'),
  ...domainFieldsSchema,
  networkID: z.string().startsWith('network-', 'Please choose a network '),
  roleID: z.union([z.literal(NO_ROLE_FLAG), z.string().startsWith('role-', 'Please choose an option')]),
  certLifetimeUnit: z.enum(LIFETIME_UNITS),
  certLifetime: z.union([z.null(), z.number().min(1, 'Must be more than zero')]), // max validation happens in refinement below
  autoAssignIPv4: z.boolean(),
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- There's probably a more elegant way to do this, but it works
const thing = z.object(endpointFieldsSchema);

function checkDuration(data: z.infer<typeof thing>) {
  const hours = convertToHours(data.certLifetimeUnit, data.certLifetime);
  return hours === null || hours <= MAX_HOURS;
}

export const editEndpointIdentityProviderFormSchema = editIdentityProviderBaseFormSchema
  .extend(endpointFieldsSchema)
  .refine(checkDuration, TOO_MANY_HOURS_ERR)
  .refine(checkNewDomain, NEW_DOMAIN_NAME_ERR)
  .refine(checkDomainFormat, INVALID_DOMAIN_NAME_ERR);
export type EditEndpointIdentityProviderFormData = z.input<typeof editEndpointIdentityProviderFormSchema>;
export type EditEndpointIdentityProviderTransformedData = z.infer<typeof editEndpointIdentityProviderFormSchema>;

export const addEndpointIdentityProviderFormSchema = addIdentityProviderBaseFormSchema
  .extend(endpointFieldsSchema)
  .refine(checkDuration, TOO_MANY_HOURS_ERR)
  .refine(checkNewDomain, NEW_DOMAIN_NAME_ERR)
  .refine(checkDomainFormat, INVALID_DOMAIN_NAME_ERR);
export type AddEndpointIdentityProviderFormData = z.input<typeof addEndpointIdentityProviderFormSchema>;
export type AddEndpointIdentityProviderTransformedData = z.infer<typeof addEndpointIdentityProviderFormSchema>;
